import { AssetTypeCode, WorkfrontMappingKind } from '@atlas/types'
import { CardShell, SectionCard, SelectField } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAssetTypeMappings } from '@/hooks/useWorkfrontMappings'
import type { AssetTypeListRow, AssetTypeRow } from '@/services/workfront-mappings.service'
import { AssetTypesTable } from './components/MappingTables'
import { useDrawerContext, useReportLoadFailure, useSelection } from './tab-support'
import { NEW_ROW_KEY, useFixLink, useMappingEditor } from './useMappingEditor'

const pairOf = (row: AssetTypeRow) => `${row.classificationValue} / ${row.assetDetailValue ?? 'none'}`

/**
 * Asset types: each (Classification, Asset Detail) pair and the asset type it
 * becomes; several rows can be filled with one code. `?classification=…&detail=…&edit=1`
 * (an `UNKNOWN_ASSET_TYPE`'s Fix) opens the pair pre-filled.
 */
export function AssetTypesTab() {
	const [params] = useSearchParams()
	const [classification, setClassification] = useState(params.get('classification') ?? '')
	const [fillCode, setFillCode] = useState('')
	const query = useAssetTypeMappings()
	const rows = query.data ?? []
	const selection = useSelection()
	const editor = useMappingEditor(WorkfrontMappingKind.ASSET_TYPE, (key) => rows.find((r) => r.key === key)?.row)
	const context = useDrawerContext()
	useReportLoadFailure(query.isError, 'asset types')

	const edit = ({ key, row, status }: AssetTypeListRow) =>
		editor.open({ key, row, title: `Edit ${pairOf(row)}`, canDelete: status !== 'deleted' })
	useFixLink(query.isSuccess, (fix) => {
		const row: AssetTypeRow = {
			classificationValue: fix.get('classification') ?? '',
			assetDetailParameterId: null,
			assetDetailValue: fix.get('detail') || null,
			assetTypeCode: '',
		}
		const found = rows.find(
			(r) => r.row.classificationValue === row.classificationValue && r.row.assetDetailValue === row.assetDetailValue,
		)
		editor.open({ key: found?.key ?? NEW_ROW_KEY, row: found?.row ?? row, title: `Map ${pairOf(row)}`, fromFix: true })
	})

	const classifications = [...new Set(rows.map((r) => r.row.classificationValue))].sort()
	const shown = rows.filter((r) => !classification || r.row.classificationValue === classification)
	const fill = () => {
		const saves = rows
			.filter((r) => selection.selected.has(r.key))
			.map((r) => ({ key: r.key, row: { ...r.row, assetTypeCode: fillCode } }))
		editor.saveMany(saves, `Draft saved: ${saves.length} rows set to ${fillCode}`, selection.clear)
	}

	return (
		<div className="space-y-4">
			<SectionCard title="Filters">
				<div className="grid gap-4 md:grid-cols-4">
					<SelectField
						label="Classification"
						value={classification}
						placeholder="any"
						options={classifications.map((value) => ({ value, label: value }))}
						onChange={setClassification}
					/>
				</div>
			</SectionCard>
			<div className="flex flex-wrap items-end gap-2">
				<Button
					size="sm"
					onPress={() =>
						editor.open({
							key: NEW_ROW_KEY,
							row: { classificationValue: '', assetDetailParameterId: null, assetDetailValue: null, assetTypeCode: '' },
							title: 'Add an asset type',
						})
					}
				>
					Add asset type
				</Button>
				<div className="w-64">
					<SelectField
						label="Code for the selected rows"
						value={fillCode}
						placeholder="choose"
						options={Object.values(AssetTypeCode).map((code) => ({
							value: code,
							label: context.assetTypeNames[code] ? `${code} · ${context.assetTypeNames[code]}` : code,
						}))}
						onChange={setFillCode}
					/>
				</div>
				<Button
					size="sm"
					variant="outline"
					isDisabled={!fillCode || !selection.selected.size || editor.saving}
					onPress={fill}
				>
					Fill selected
				</Button>
			</div>
			<CardShell>
				<AssetTypesTable
					rows={shown}
					filtered={!!classification}
					assetTypeNames={context.assetTypeNames}
					isLoading={query.isLoading}
					isError={query.isError}
					onRetry={() => void query.refetch()}
					selected={selection.selected}
					onToggle={selection.onToggle}
					onEdit={edit}
				/>
			</CardShell>
			{editor.drawer(context)}
		</div>
	)
}
